import React from 'react';
import { TrendingUp, TrendingDown, Minus, ChevronUp, ChevronDown, ArrowRight } from 'lucide-react';
import type { RankingEntry } from '../../types/analytics';

interface RankingTableProps {
  data: RankingEntry[];
  title?: string;
  showTrend?: boolean;
  showRankChange?: boolean;
  limit?: number;
  onRowClick?: (entry: RankingEntry) => void;
  highlightTop?: number;
  highlightBottom?: number;
  emptyMessage?: string;
}

const RankingTable: React.FC<RankingTableProps> = ({
  data,
  title,
  showTrend = true,
  showRankChange = true,
  limit = 10,
  onRowClick,
  highlightTop = 3,
  highlightBottom = 0,
  emptyMessage = 'No data available',
}) => {
  const displayData = data.slice(0, limit);
  const totalEntries = data.length;

  const getRankBadge = (rank: number) => {
    if (rank === 1) return 'bg-yellow-400 text-yellow-900';
    if (rank === 2) return 'bg-gray-300 text-gray-800';
    if (rank === 3) return 'bg-amber-600 text-white';
    return 'bg-gray-100 text-gray-600';
  };

  const getPerformanceColor = (percent: number) => {
    if (percent >= 95) return 'text-green-600';
    if (percent >= 85) return 'text-green-500';
    if (percent >= 70) return 'text-amber-500';
    if (percent >= 50) return 'text-orange-500';
    return 'text-red-500';
  };

  const getProgressBarColor = (percent: number) => {
    if (percent >= 95) return 'bg-green-500';
    if (percent >= 85) return 'bg-green-400';
    if (percent >= 70) return 'bg-amber-400';
    if (percent >= 50) return 'bg-orange-400';
    return 'bg-red-400';
  };

  const getTrendIcon = (direction: 'up' | 'down' | 'stable') => {
    switch (direction) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      default:
        return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const getRankChangeIcon = (change: number) => {
    if (change > 0) {
      return (
        <span className="flex items-center gap-0.5 text-green-600 text-xs font-medium">
          <ChevronUp className="w-3 h-3" />
          {change}
        </span>
      );
    }
    if (change < 0) {
      return (
        <span className="flex items-center gap-0.5 text-red-600 text-xs font-medium">
          <ChevronDown className="w-3 h-3" />
          {Math.abs(change)}
        </span>
      );
    }
    return <ArrowRight className="w-3 h-3 text-gray-400" />;
  };

  const getRowHighlight = (rank: number) => {
    if (rank <= highlightTop) return 'bg-green-50 hover:bg-green-100';
    if (highlightBottom > 0 && rank > totalEntries - highlightBottom) return 'bg-red-50 hover:bg-red-100';
    return 'hover:bg-gray-50';
  };

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {title && (
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        )}
        <div className="flex items-center justify-center py-8 text-gray-500">
          {emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {title && (
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
      )}
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-16">
                Rank
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                Name
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase w-40">
                Attendance
              </th>
              {showTrend && (
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase w-24">
                  Trend
                </th>
              )}
              {showRankChange && (
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase w-20">
                  Change
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {displayData.map((entry) => (
              <tr 
                key={`${entry.type}-${entry.id}`}
                className={`${getRowHighlight(entry.rank)} ${onRowClick ? 'cursor-pointer' : ''} transition-colors`}
                onClick={() => onRowClick?.(entry)}
              >
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${getRankBadge(entry.rank)}`}>
                    {entry.rank}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{entry.name}</p>
                    {entry.parentName && (
                      <p className="text-xs text-gray-500">{entry.parentName}</p>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${getProgressBarColor(entry.attendancePercent ?? 0)}`}
                        style={{ width: `${entry.attendancePercent ?? 0}%` }}
                      />
                    </div>
                    <span className={`text-sm font-semibold min-w-[48px] text-right ${getPerformanceColor(entry.attendancePercent ?? 0)}`}>
                      {(entry.attendancePercent ?? 0).toFixed(1)}%
                    </span>
                  </div>
                </td>
                {showTrend && (
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      {getTrendIcon(entry.trendDirection)}
                      <span className={`text-xs font-medium ${
                        entry.trendDirection === 'up' ? 'text-green-600' :
                        entry.trendDirection === 'down' ? 'text-red-600' :
                        'text-gray-500'
                      }`}>
                        {(entry.trendPercent ?? 0) >= 0 ? '+' : ''}{(entry.trendPercent ?? 0).toFixed(1)}%
                      </span>
                    </div>
                  </td>
                )}
                {showRankChange && (
                  <td className="px-4 py-3 text-center">
                    {getRankChangeIcon(entry.rankChange)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.length > limit && (
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 text-center">
          <span className="text-sm text-gray-500">
            Showing {limit} of {data.length} entries
          </span>
        </div>
      )}
    </div>
  );
};

export default RankingTable;
